import { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import { OutwriteApi } from '../services/OutwriteService';
import { axiosClient } from '../api/proxy/AxiosClient';
import OutwriteCollapsibleTable from './OutwriteCollapsibleTable';

const USER_TYPE_OPTIONS = [
  { value: '', label: 'All types' },
  { value: 'outwrite_learner', label: 'outwrite_learner' },
  { value: 'outwrite', label: 'outwrite' },
];

const TABLE_LIMIT = 500;
const DOWNLOAD_LIMIT = 5000;

const SELECTED_ROW_BG = '#e3f2fd';

function formatUserLabel(user) {
  if (!user) return '';
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  if (name) return `${name} (${user.email || user.uid})`;
  return user.email || user.uid || `id=${user.id}`;
}

function formatWhen(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
}

function truncate(text, max = 80) {
  const s = text == null ? '' : String(text);
  if (s.length <= max) return s;
  return `${s.slice(0, max)}…`;
}

function formatKeyValue(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function formatExplanation(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function logRowKey(row) {
  return row?.correlationId || String(row?.id ?? '');
}

function buildLogParams(filters, { limit, includeRaw }) {
  const params = { limit };
  if (includeRaw) params.includeRaw = true;
  if (filters.userType) params.userType = filters.userType;
  if (filters.userId) params.userId = filters.userId;
  if (filters.from) params.from = filters.from;
  if (filters.to) params.to = filters.to;
  return params;
}

/**
 * Outwrite LOGS: filter + Generate table + Download JSON of check audit rows.
 * Click a log row to fill detail tables (rubrics / to_finds / keys / assessment).
 */
export default function OutwriteLogs() {
  const [userType, setUserType] = useState('');
  const [userId, setUserId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [actors, setActors] = useState([]);
  const [nameById, setNameById] = useState({});
  const [rows, setRows] = useState([]);
  const [selectedKey, setSelectedKey] = useState('');
  const [actorsLoading, setActorsLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [generated, setGenerated] = useState(false);

  const filters = useMemo(
    () => ({
      userType: userType || undefined,
      userId: userId || undefined,
      from: from || undefined,
      to: to || undefined,
    }),
    [userType, userId, from, to],
  );

  const selectedLog = useMemo(
    () => rows.find((row) => logRowKey(row) === selectedKey) || null,
    [rows, selectedKey],
  );

  const rubricsUsed = useMemo(
    () => (Array.isArray(selectedLog?.rubricsUsed) ? selectedLog.rubricsUsed : []),
    [selectedLog],
  );

  const toFinds = useMemo(
    () => (Array.isArray(selectedLog?.toFinds) ? selectedLog.toFinds : []),
    [selectedLog],
  );

  const keys = useMemo(() => {
    const snapshotKeys = selectedLog?.taskSnapshot?.keys;
    return Array.isArray(snapshotKeys) ? snapshotKeys : [];
  }, [selectedLog]);

  const assessmentResults = useMemo(
    () =>
      Array.isArray(selectedLog?.assessmentResults)
        ? selectedLog.assessmentResults
        : [],
    [selectedLog],
  );

  const annotationByRubricId = useMemo(() => {
    const map = new Map();
    (Array.isArray(selectedLog?.annotations) ? selectedLog.annotations : []).forEach(
      (row) => {
        if (!row?.rubric_id) return;
        map.set(row.rubric_id, {
          expected: row.expected !== false,
          comment: row.comment ?? '',
        });
      },
    );
    return map;
  }, [selectedLog]);

  const ruleById = useMemo(() => {
    const map = new Map();
    rubricsUsed.forEach((r) => {
      if (r?.id) map.set(r.id, r.rule || '');
    });
    return map;
  }, [rubricsUsed]);

  const loadActors = useCallback(() => {
    setActorsLoading(true);
    OutwriteApi.listCheckLogActors()
      .then(async (list) => {
        const nextActors = Array.isArray(list) ? list : [];
        setActors(nextActors);
        const ids = [...new Set(nextActors.map((a) => a.userId).filter(Boolean))];
        if (ids.length === 0) {
          setNameById({});
          return;
        }
        try {
          const users = await axiosClient
            .get('/users/lookup-by-ids', { params: { ids: ids.join(',') } })
            .then((r) => r.data);
          const map = {};
          (Array.isArray(users) ? users : []).forEach((u) => {
            map[u.id] = u;
          });
          setNameById(map);
        } catch {
          setNameById({});
        }
      })
      .catch(() => {
        setActors([]);
        setNameById({});
      })
      .finally(() => setActorsLoading(false));
  }, []);

  useEffect(() => {
    loadActors();
  }, [loadActors]);

  const userOptions = useMemo(() => {
    const byType = userType
      ? actors.filter((a) => a.userType === userType)
      : actors;
    return byType.map((a) => ({
      value: String(a.userId),
      label: nameById[a.userId]
        ? formatUserLabel(nameById[a.userId])
        : `${a.userUid} (id=${a.userId})`,
    }));
  }, [actors, nameById, userType]);

  useEffect(() => {
    if (userId && !userOptions.some((o) => o.value === userId)) {
      setUserId('');
    }
  }, [userOptions, userId]);

  const handleGenerate = () => {
    if (busy) return;
    setBusy(true);
    setError('');
    setSelectedKey('');
    OutwriteApi.listCheckLogs(buildLogParams(filters, { limit: TABLE_LIMIT }))
      .then((list) => {
        setRows(Array.isArray(list) ? list : []);
        setGenerated(true);
      })
      .catch((err) => {
        setRows([]);
        setGenerated(true);
        setError(err?.response?.data?.message || err?.message || 'Failed to load logs');
      })
      .finally(() => setBusy(false));
  };

  const handleDownload = () => {
    if (busy) return;
    setBusy(true);
    setError('');
    OutwriteApi.listCheckLogs(
      buildLogParams(filters, { limit: DOWNLOAD_LIMIT, includeRaw: true }),
    )
      .then((list) => {
        const payload = Array.isArray(list) ? list : [];
        const enriched = payload.map((row) => ({
          ...row,
          userDisplayName: nameById[row.userId]
            ? formatUserLabel(nameById[row.userId])
            : row.userUid,
        }));
        const blob = new Blob([JSON.stringify(enriched, null, 2)], {
          type: 'application/json',
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
        a.href = url;
        a.download = `outwrite-check-logs-${stamp}.json`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      })
      .catch((err) => {
        setError(
          err?.response?.data?.message || err?.message || 'Failed to download logs',
        );
      })
      .finally(() => setBusy(false));
  };

  const detailKey = selectedKey || 'none';

  return (
    <Box sx={{ p: 3, maxWidth: 1280, mx: 'auto' }}>
      <Typography variant="h5" component="h1" sx={{ mb: 0.5 }}>
        Logs
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Check audit rows (`POST /outwrite/check`). Generate loads the table; Download exports
        matching rows as JSON (includes raw agent text). Click a row to fill detail tables
        below.
      </Typography>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        alignItems={{ md: 'center' }}
        sx={{ mb: 2, flexWrap: 'wrap' }}
      >
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="outwrite-logs-type-label">User type</InputLabel>
          <Select
            labelId="outwrite-logs-type-label"
            label="User type"
            value={userType}
            onChange={(e) => setUserType(e.target.value)}
            disabled={busy}
          >
            {USER_TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value || 'all'} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel id="outwrite-logs-user-label">User</InputLabel>
          <Select
            labelId="outwrite-logs-user-label"
            label="User"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            disabled={busy || actorsLoading}
          >
            <MenuItem value="">
              <em>All users</em>
            </MenuItem>
            {userOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          type="date"
          label="From"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          disabled={busy}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 160 }}
        />
        <TextField
          size="small"
          type="date"
          label="To"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          disabled={busy}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 160 }}
        />

        <Button
          variant="contained"
          color="primary"
          onClick={handleGenerate}
          disabled={busy}
        >
          Generate
        </Button>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleDownload}
          disabled={busy}
        >
          Download
        </Button>
        {busy && <CircularProgress size={22} />}
      </Stack>

      {error && (
        <Typography color="error" variant="body2" sx={{ mb: 1.5 }}>
          {error}
        </Typography>
      )}

      {!generated && !busy && (
        <Typography variant="body2" color="text.secondary">
          Set filters and press Generate to load the table.
        </Typography>
      )}

      {generated && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Started</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Task</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Model</TableCell>
                <TableCell align="right">ms</TableCell>
                <TableCell>Answer</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Typography variant="body2" color="text.secondary">
                      No rows for the current filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const key = logRowKey(row);
                  const selected = key === selectedKey;
                  return (
                    <TableRow
                      key={key}
                      hover
                      selected={selected}
                      onClick={() => setSelectedKey(key)}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: selected ? SELECTED_ROW_BG : undefined,
                      }}
                    >
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatWhen(row.startedAt)}
                      </TableCell>
                      <TableCell>
                        {nameById[row.userId]
                          ? formatUserLabel(nameById[row.userId])
                          : row.userUid}
                      </TableCell>
                      <TableCell>{row.userType}</TableCell>
                      <TableCell sx={{ maxWidth: 220 }} title={row.taskId}>
                        {truncate(row.taskId, 40)}
                      </TableCell>
                      <TableCell>{row.status}</TableCell>
                      <TableCell
                        sx={{ maxWidth: 160 }}
                        title={row.llmModelId || ''}
                      >
                        {truncate(row.llmModelId || '—', 28)}
                      </TableCell>
                      <TableCell align="right">{row.durationMs ?? '—'}</TableCell>
                      <TableCell sx={{ maxWidth: 280 }} title={row.answerText}>
                        {truncate(row.answerText, 60)}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {generated && rows.length > 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, mb: 2, display: 'block' }}
        >
          Showing {rows.length} row{rows.length === 1 ? '' : 's'}
          {rows.length >= TABLE_LIMIT ? ` (table capped at ${TABLE_LIMIT})` : ''}.
          {selectedLog
            ? ` Selected: ${selectedLog.taskId} @ ${formatWhen(selectedLog.startedAt)}.`
            : ' Click a row to inspect rubrics, to_finds, keys, and assessment.'}
        </Typography>
      )}

      {generated && (
        <Stack spacing={2} sx={{ mt: 1 }}>
          <OutwriteCollapsibleTable
            key={`rubrics-${detailKey}`}
            title="Rubrics used"
            summary={
              selectedLog
                ? rubricsUsed.length === 1
                  ? '1 item'
                  : `${rubricsUsed.length} items`
                : 'select a log row'
            }
            defaultExpanded={false}
            columns={[
              { key: 'rubric_id', label: 'rubric_id', width: '20%' },
              { key: 'rule', label: 'rule' },
            ]}
          >
            {rubricsUsed.map((row, index) => (
              <TableRow key={`${row.id || 'r'}-${index}`}>
                <TableCell>{row.id}</TableCell>
                <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{row.rule || ''}</TableCell>
              </TableRow>
            ))}
            {selectedLog && rubricsUsed.length === 0 && (
              <TableRow>
                <TableCell colSpan={2}>
                  <Typography variant="body2" color="text.secondary">
                    No rubrics stored on this log.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!selectedLog && (
              <TableRow>
                <TableCell colSpan={2}>
                  <Typography variant="body2" color="text.secondary">
                    Select a log row above.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </OutwriteCollapsibleTable>

          <OutwriteCollapsibleTable
            key={`tofinds-${detailKey}`}
            title="ToFinds"
            summary={
              selectedLog
                ? toFinds.length === 1
                  ? '1 item'
                  : `${toFinds.length} items`
                : 'select a log row'
            }
            defaultExpanded={false}
            columns={[
              { key: 'id', label: 'id', width: '14%' },
              { key: 'key_id', label: 'key_id', width: '12%' },
              { key: 'prompt', label: 'prompt', width: '28%' },
              { key: 'options', label: 'options' },
              { key: 'explanation', label: 'explanation', width: '18%' },
            ]}
          >
            {toFinds.map((row, index) => (
              <TableRow key={`${row.id || 'tf'}-${index}`}>
                <TableCell>{row.id}</TableCell>
                <TableCell>{row.key_id || ''}</TableCell>
                <TableCell sx={{ whiteSpace: 'pre-wrap' }}>
                  {row.prompt || ''}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'pre-wrap' }}>
                  {(row.options || []).join('\n')}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>
                  {formatExplanation(row.explanation)}
                </TableCell>
              </TableRow>
            ))}
            {selectedLog && toFinds.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary">
                    No to_finds stored on this log (older rows may predate V33).
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!selectedLog && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary">
                    Select a log row above.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </OutwriteCollapsibleTable>

          <OutwriteCollapsibleTable
            key={`keys-${detailKey}`}
            title="Keys"
            summary={
              selectedLog
                ? keys.length === 1
                  ? '1 key'
                  : `${keys.length} keys`
                : 'select a log row'
            }
            defaultExpanded={false}
            columns={[
              { key: 'id', label: 'id', width: '22%' },
              { key: 'value', label: 'value' },
            ]}
          >
            {keys.map((key) => (
              <TableRow key={key.id}>
                <TableCell>{key.id}</TableCell>
                <TableCell
                  sx={{
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'monospace',
                    fontSize: 12,
                  }}
                >
                  {formatKeyValue(key.value)}
                </TableCell>
              </TableRow>
            ))}
            {selectedLog && keys.length === 0 && (
              <TableRow>
                <TableCell colSpan={2}>
                  <Typography variant="body2" color="text.secondary">
                    No keys in this log&apos;s task snapshot.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!selectedLog && (
              <TableRow>
                <TableCell colSpan={2}>
                  <Typography variant="body2" color="text.secondary">
                    Select a log row above.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </OutwriteCollapsibleTable>

          <OutwriteCollapsibleTable
            key={`assessment-${detailKey}`}
            title="Assessment results"
            summary={
              selectedLog
                ? assessmentResults.length === 1
                  ? '1 result'
                  : `${assessmentResults.length} results`
                : 'select a log row'
            }
            defaultExpanded={false}
            columns={[
              { key: 'rubric_id', label: 'rubric_id' },
              { key: 'rule', label: 'rule' },
              { key: 'result', label: 'result', width: 90 },
              { key: 'failed_at', label: 'failed_at' },
              { key: 'note', label: 'note' },
              { key: 'expected', label: 'Expected', width: 100 },
              { key: 'comment', label: 'Comment' },
            ]}
          >
            {assessmentResults.map((row, index) => {
              const result = String(row.result || '').toUpperCase();
              const isPass = result === 'PASS';
              const isFail = result === 'FAIL';
              const ann = annotationByRubricId.get(row.rubric_id) || {
                expected: true,
                comment: '',
              };
              return (
                <TableRow key={`${row.rubric_id}-${index}`}>
                  <TableCell>{row.rubric_id}</TableCell>
                  <TableCell sx={{ whiteSpace: 'pre-wrap' }}>
                    {ruleById.get(row.rubric_id) || ''}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: isPass
                        ? 'success.main'
                        : isFail
                          ? 'error.main'
                          : 'text.primary',
                    }}
                  >
                    {row.result}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'pre-wrap' }}>
                    {Array.isArray(row.failed_at) ? row.failed_at.join('\n') : ''}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'pre-wrap' }}>
                    {row.note || ''}
                  </TableCell>
                  <TableCell>
                    {ann.expected ? 'true' : 'false'}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'pre-wrap' }}>
                    {ann.comment || ''}
                  </TableCell>
                </TableRow>
              );
            })}
            {selectedLog && assessmentResults.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="text.secondary">
                    No assessment results on this log.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!selectedLog && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="text.secondary">
                    Select a log row above.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </OutwriteCollapsibleTable>
        </Stack>
      )}
    </Box>
  );
}
